<template>
  <Page>
    <div class="flex flex-col gap-2 h-full min-h-0 min-w-0">
      <h1 class="text-base font-semibold text-text lg:sr-only">Maintenance Analysis</h1>

      <!-- Filter card island -->
      <div class="bg-background-card rounded-lg shadow-card p-3 border border-border hover:shadow-card-hover transition-shadow shrink-0">
        <div class="flex flex-col gap-3">
          <!-- Row 1: Search -->
          <div class="relative">
            <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-secondary text-sm pointer-events-none"></i>
            <InputText
              v-model="searchQuery"
              placeholder="Search by analysis ID, reference, or reference type..."
              class="w-full pl-9"
              size="small"
            />
          </div>

          <!-- Row 2: Filter chips -->
          <div class="flex flex-wrap items-center gap-2">
            <!-- Methodology chips -->
            <span class="text-xs font-semibold text-text-muted mr-1">Methodology</span>
            <button
              v-for="m in allMethodologies"
              :key="m"
              type="button"
              :class="[
                'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-semibold transition-all border',
                methodologyFilters.includes(m)
                  ? methodologyActiveClass(m)
                  : 'border-border text-text-muted hover:border-primary/40 hover:text-text bg-transparent',
              ]"
              @click="toggleMethodology(m)"
            >
              {{ m }}
            </button>

            <span class="w-px h-5 bg-border mx-1"></span>

            <!-- Status filter -->
            <span class="text-xs font-semibold text-text-muted mr-1">Status</span>
            <button
              v-for="s in allStatuses"
              :key="s"
              type="button"
              :class="[
                'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-semibold transition-all border',
                statusFilters.includes(s)
                  ? statusActiveClass(s)
                  : 'border-border text-text-muted hover:border-primary/40 hover:text-text bg-transparent',
              ]"
              @click="toggleStatus(s)"
            >
              <span :class="['w-2 h-2 rounded-full', statusDotClass(s)]"></span>
              {{ s }}
            </button>

            <!-- Clear all -->
            <button
              v-if="hasActiveFilters"
              type="button"
              class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-semibold border border-accent/40 text-accent hover:bg-accent hover:text-text-inverse transition-all ml-auto"
              @click="clearFilters"
            >
              <i class="fa-solid fa-filter-circle-xmark text-[0.6rem]"></i>
              Clear all
            </button>
          </div>
        </div>
      </div>

      <!-- Table card island -->
      <div class="flex-1 min-h-0">
        <div class="bg-background-card rounded-lg shadow-card border border-border hover:shadow-card-hover transition-shadow h-full flex flex-col overflow-hidden">
          <!-- Table header bar -->
          <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-2 border-b border-border shrink-0">
            <div class="flex items-center gap-2">
              <i class="fa-solid fa-table-list text-primary text-sm" aria-hidden="true"></i>
              <span class="text-sm font-semibold text-text">Analyses</span>
              <span v-if="totalItems > 0" class="text-xs text-text-light tabular-nums">
                {{ totalItems.toLocaleString() }} {{ totalItems === 1 ? 'result' : 'results' }}
              </span>
            </div>
            <div role="group" aria-label="Analysis actions" class="flex flex-wrap items-center gap-2">
              <button
                type="button"
                :class="[
                  'inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full text-xs font-semibold border transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  showDeleted
                    ? 'bg-secondary/15 border-secondary/40 text-secondary font-bold'
                    : 'border-border text-text-muted hover:border-primary/40 hover:text-text bg-transparent',
                ]"
                title="Toggle deleted analyses"
                :aria-pressed="showDeleted"
                @click="showDeleted = !showDeleted"
              >
                <i class="fa-solid fa-trash-can text-[0.6rem]" aria-hidden="true"></i>
                Deleted
              </button>
              <button
                type="button"
                class="w-8 h-8 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                :disabled="isLoading"
                title="Refresh"
                aria-label="Refresh analyses"
                @click="fetchPage()"
              >
                <i class="fa-solid fa-rotate text-xs" :class="{ 'fa-spin': isLoading }" aria-hidden="true"></i>
              </button>
              <Button
                label="Import via Load Sheet"
                icon="fa-solid fa-file-import"
                severity="secondary"
                outlined
                size="small"
                v-tooltip.top="loadSheetImportTooltip"
                @click="goToLoadSheetImport"
              />
              <Button
                label="Start New Analysis"
                icon="fa-solid fa-plus"
                severity="primary"
                size="small"
                @click="showMethodologyPicker = true"
              />
            </div>
          </div>

          <!-- Loading skeleton -->
          <template v-if="isLoading && items.length === 0">
            <div class="flex-1 p-4 space-y-0">
              <!-- Skeleton header row -->
              <div class="flex items-center gap-4 px-2 pb-3 border-b border-border/50 mb-3">
                <div class="h-3 bg-border/40 rounded w-24"></div>
                <div class="h-3 bg-border/40 rounded w-16"></div>
                <div class="h-3 bg-border/40 rounded w-28"></div>
                <div class="h-3 bg-border/40 rounded w-24"></div>
                <div class="h-3 bg-border/40 rounded w-16"></div>
                <div class="h-3 bg-border/40 rounded w-16"></div>
                <div class="h-3 bg-border/40 rounded w-14"></div>
                <div class="h-3 bg-border/40 rounded w-20 ml-auto"></div>
              </div>
              <!-- Skeleton data rows -->
              <div v-for="n in 8" :key="n" class="flex items-center gap-4 px-2 py-2.5 animate-pulse">
                <div class="h-4 bg-border/60 rounded w-28"></div>
                <div class="h-5 bg-border/60 rounded-full w-12"></div>
                <div class="h-4 bg-border/60 rounded w-40"></div>
                <div class="h-4 bg-border/60 rounded w-28"></div>
                <div class="flex items-center gap-1.5">
                  <div class="w-2 h-2 bg-border/60 rounded-full"></div>
                  <div class="h-4 bg-border/60 rounded w-16"></div>
                </div>
                <div class="flex items-center gap-1.5">
                  <div class="w-2 h-2 bg-border/60 rounded-full"></div>
                  <div class="h-4 bg-border/60 rounded w-14"></div>
                </div>
                <div class="h-4 bg-border/60 rounded w-10"></div>
                <div class="h-4 bg-border/60 rounded w-20 ml-auto"></div>
                <div class="flex items-center gap-1 ml-auto">
                  <div class="h-7 bg-border/60 rounded w-7"></div>
                  <div class="h-7 bg-border/60 rounded w-16"></div>
                  <div class="h-7 bg-border/60 rounded w-7"></div>
                </div>
              </div>
            </div>
          </template>

          <!-- Empty state -->
          <template v-else-if="!isLoading && items.length === 0">
            <div class="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
              <div class="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <i class="fa-solid fa-microscope text-xl text-primary/60"></i>
              </div>
              <template v-if="hasActiveFilters">
                <p class="text-sm font-semibold text-text">No analyses match your filters</p>
                <p class="text-xs text-text-muted max-w-sm">
                  Try adjusting or clearing your filters to see more results.
                </p>
                <button
                  type="button"
                  class="mt-1 inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold border border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                  @click="clearFilters"
                >
                  <i class="fa-solid fa-filter-circle-xmark text-[0.6rem]"></i>
                  Clear filters
                </button>
              </template>
              <template v-else>
                <p class="text-sm font-semibold text-text">No maintenance analyses yet</p>
                <p class="text-xs text-text-muted max-w-sm">
                  Start a new analysis to begin documenting maintenance strategies for your assets.
                </p>
                <Button
                  label="Start New Analysis"
                  icon="fa-solid fa-plus"
                  severity="primary"
                  size="small"
                  class="mt-1"
                  @click="showMethodologyPicker = true"
                />
              </template>
            </div>
          </template>

          <!-- Data table -->
          <template v-else>
            <div class="flex-1 min-h-0 flex flex-col overflow-hidden">
            <DataTable
              :value="items"
              :loading="isLoading"
              scrollable
              scrollHeight="flex"
              stripedRows
              showGridlines
              class="p-datatable-sm flex-1 min-h-0"
              dataKey="id"
              :row-class="deletedRowClass"
              @sort="onTableSort"
              :sort-field="sortField"
              :sort-order="sortOrder"
            >
              <Column field="analysisNumber" header="Analysis ID" sortable style="min-width: 160px">
                <template #body="{ data }">
                  <button
                    type="button"
                    class="text-primary hover:underline font-semibold text-left text-sm"
                    @click="navigateToEdit(data)"
                  >
                    {{ data.analysisNumber }}
                  </button>
                </template>
              </Column>
              <Column field="methodology" header="Methodology" sortable style="min-width: 110px">
                <template #body="{ data }">
                  <span
                    :class="[
                      'inline-flex items-center h-6 px-2 rounded-full text-[0.7rem] font-bold border',
                      methodologyBadgeClass(data.methodology),
                    ]"
                  >
                    {{ data.methodology }}
                  </span>
                </template>
              </Column>
              <Column field="referenceLabel" header="Reference" sortable style="min-width: 200px">
                <template #body="{ data }">
                  <span class="text-sm text-text truncate block max-w-[20rem]">{{ data.referenceLabel }}</span>
                </template>
              </Column>
              <Column field="referenceType" header="Reference Type" sortable style="min-width: 150px">
                <template #body="{ data }">
                  <span class="text-sm text-text-muted">{{ data.referenceType }}</span>
                </template>
              </Column>
              <Column field="analysisStatus" header="Status" sortable style="min-width: 130px">
                <template #body="{ data }">
                  <div class="flex items-center gap-1.5">
                    <span :class="['w-2 h-2 rounded-full', statusDotClass(data.analysisStatus)]"></span>
                    <span class="text-sm font-medium">{{ data.analysisStatus }}</span>
                  </div>
                </template>
              </Column>
              <Column field="revision" header="Revision" sortable style="min-width: 90px" />
              <Column field="entriesCount" header="Entries" style="min-width: 80px; text-align: center">
                <template #body="{ data }">
                  <span class="text-sm font-medium tabular-nums">{{ data.entriesCount }}</span>
                </template>
              </Column>
              <Column field="lastUpdated" header="Last Updated" sortable style="min-width: 120px">
                <template #body="{ data }">
                  <span class="text-sm text-text-muted">{{ data.lastUpdated }}</span>
                </template>
              </Column>
              <Column header="Actions" style="min-width: 140px" frozen alignFrozen="right">
                <template #body="{ data }">
                  <div class="flex items-center gap-1">
                    <Button
                      icon="fa-solid fa-link"
                      severity="secondary"
                      text
                      size="small"
                      v-tooltip.bottom="'Review Assignments'"
                      :disabled="!data.analysisId"
                      @click="navigateToAssignmentReview(data)"
                    />
                    <Button
                      v-if="data.isActive === false"
                      icon="fa-solid fa-rotate-left"
                      severity="success"
                      text
                      size="small"
                      v-tooltip.bottom="'Restore Analysis'"
                      :disabled="!data.analysisId"
                      @click="confirmRestore(data)"
                    />
                    <Button
                      label="Open"
                      icon="fa-solid fa-arrow-right"
                      severity="primary"
                      text
                      size="small"
                      @click="navigateToEdit(data)"
                    />
                    <Button
                      v-if="data.isActive !== false"
                      icon="fa-solid fa-trash"
                      severity="danger"
                      text
                      size="small"
                      v-tooltip.bottom="'Delete Analysis'"
                      :disabled="!data.analysisId"
                      @click="confirmDelete(data)"
                    />
                  </div>
                </template>
              </Column>
            </DataTable>

            <!-- Paginator -->
            <Paginator
              :current-page="currentPage"
              :page-count="pageCount"
              :page-size="pageSize"
              :total-items="totalItems"
              item-label="analyses"
              class="shrink-0"
              @update:current-page="setPage"
              @first="setPage(1)"
              @prev="setPage(currentPage - 1)"
              @next="setPage(currentPage + 1)"
              @last="setPage(pageCount)"
            />
            </div>
          </template>

          <!-- Error banner -->
          <div
            v-if="error"
            class="flex items-center gap-2 px-4 py-2 bg-error/10 border-t border-error/20 text-error text-xs shrink-0"
          >
            <i class="fa-solid fa-circle-exclamation"></i>
            <span>{{ error }}</span>
            <button
              type="button"
              class="ml-auto text-error underline hover:no-underline"
              @click="fetchPage()"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    </div>
  </Page>

  <MethodologyPickerDialog
    v-model:visible="showMethodologyPicker"
    @confirm="handleNewAnalysis"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import Page from '@/shared/components/layouts/Page.vue'
import DataTable from 'primevue/datatable'
import type { DataTableSortEvent } from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Paginator from '@/shared/components/ui/Paginator.vue'
import MethodologyPickerDialog from '@/features/maintenance/components/dialogs/MethodologyPickerDialog.vue'
import { useMaintenanceStore } from '@/features/maintenance/stores/useMaintenanceStore'
import { useAnalysisList } from '@/features/maintenance/composables/useAnalysisList'
import type { MaintainableAssetView } from '@/features/maintenance/stores/maintenanceViewModels'
import type { MethodologyType } from '@/features/maintenance/types'
import { methodologyBadgeClass } from '@/features/maintenance/utils/methodologyLabels'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { addToast } from '@/shared/utils/toastMessages'
import { formatApiError } from '@/shared/utils/errorFormatters'

definePage({ meta: { title: 'Maintenance Analysis' } })

const store = useMaintenanceStore()
const router = useRouter()
const toast = useToast()
const confirm = useConfirm()
const showMethodologyPicker = ref(false)

const {
  items,
  totalItems,
  isLoading,
  error,
  searchQuery,
  methodologyFilters,
  statusFilters,
  showDeleted,
  currentPage,
  pageSize,
  sortField,
  sortOrder,
  hasActiveFilters,
  fetchPage,
  setPage,
  onSort,
  toggleMethodology,
  toggleStatus,
  clearFilters,
} = useAnalysisList()

/**
 * Load sheets are named per methodology, so the import button targets the sheet for the single
 * active methodology filter. With none or several selected there is no unambiguous sheet, so the
 * user lands on the picker instead.
 */
const loadSheetImportMethodology = computed<MethodologyType | null>(() =>
  methodologyFilters.value.length === 1 ? methodologyFilters.value[0] : null
)

const loadSheetImportTooltip = computed(() =>
  loadSheetImportMethodology.value
    ? `Import ${loadSheetImportMethodology.value} analyses and entries from a spreadsheet`
    : 'Import analyses from a spreadsheet. Filter by one methodology to jump straight to its sheet.'
)

function goToLoadSheetImport(): void {
  const methodology = loadSheetImportMethodology.value
  void router.push({
    path: '/load-sheet',
    // Sheet names are PascalCase per methodology, e.g. RCM -> RcmAnalysis.
    query: methodology
      ? { entityType: `${methodology.charAt(0)}${methodology.slice(1).toLowerCase()}Analysis` }
      : {}
  })
}

// ---- Constants ----

const allMethodologies: MethodologyType[] = ['RCM', 'FMEA', 'RBI', 'SIL', 'GMS', 'OPS', 'OEM']
const allStatuses = ['Complete', 'In Progress', 'Not Started'] as const

// ---- Computed ----

const pageCount = computed(() => Math.max(1, Math.ceil(totalItems.value / pageSize.value)))

// ---- Lifecycle ----

onMounted(() => {
  void fetchPage()
})

// ---- Table sort handler ----

function onTableSort(event: DataTableSortEvent): void {
  if (typeof event.sortField === 'string' && event.sortOrder != null) {
    onSort(event.sortField, event.sortOrder === 1 ? 1 : -1)
  }
}

// ---- Styling helpers ----

function deletedRowClass(data: MaintainableAssetView): string | undefined {
  return data.isActive === false ? 'opacity-50' : undefined
}

function methodologyActiveClass(m: MethodologyType): string {
  switch (m) {
    case 'RCM': return 'bg-info/15 border-info/40 text-info font-bold'
    case 'FMEA': return 'bg-warning/15 border-warning/40 text-warning font-bold'
    case 'RBI': return 'bg-success/15 border-success/40 text-success font-bold'
    case 'SIL': return 'bg-error/15 border-error/40 text-error font-bold'
    case 'GMS': return 'bg-secondary/15 border-secondary/40 text-secondary font-bold'
    case 'OPS': return 'bg-accent/15 border-accent/40 text-accent font-bold'
    case 'OEM': return 'bg-primary/15 border-primary/40 text-primary font-bold'
  }
}

function statusDotClass(status: string): string {
  switch (status) {
    case 'Complete': return 'bg-green-500 dark:bg-green-400'
    case 'In Progress': return 'bg-blue-500 dark:bg-blue-400'
    default: return 'bg-gray-400 dark:bg-gray-500'
  }
}

function statusActiveClass(status: string): string {
  switch (status) {
    case 'Complete': return 'bg-success/15 border-success/40 text-success font-bold'
    case 'In Progress': return 'bg-info/15 border-info/40 text-info font-bold'
    default: return 'bg-secondary/15 border-secondary/40 text-secondary font-bold'
  }
}

// ---- Navigation ----

function navigateToAssignmentReview(asset: MaintainableAssetView): void {
  if (!asset.analysisId) return
  router.push({ path: '/maintenance/assign', query: { analysisId: asset.analysisId } })
}

function navigateToEdit(asset: MaintainableAssetView): void {
  router.push({
    path: '/maintenance/analysis/edit',
    query: asset.analysisId
      ? { analysisId: asset.analysisId }
      : asset.reference?.kind === 'Asset' && asset.reference.assetId
        ? { assetId: asset.reference.assetId }
        : undefined,
  })
}

function handleNewAnalysis(payload: { methodology: MethodologyType }): void {
  router.push({
    path: '/maintenance/analysis/edit',
    query: { mode: 'new', methodology: payload.methodology },
  })
}

// ---- Delete ----

function confirmDelete(asset: MaintainableAssetView): void {
  if (!asset.analysisId) return

  confirm.require({
    header: 'Delete Analysis',
    message: `Are you sure you want to delete analysis ${asset.analysisNumber}? All of its entries will also be deleted.`,
    icon: 'fa-solid fa-triangle-exclamation',
    acceptLabel: 'Delete',
    rejectLabel: 'Cancel',
    acceptClass: 'p-button-danger',
    accept: async () => {
      try {
        await store.deleteAnalysis(asset.analysisId!)
        addToast(toast, {
          severity: 'success',
          summary: 'Analysis deleted',
          detail: `${asset.analysisNumber} was deleted.`,
        })
        // Refetch current page to reflect the deletion
        await fetchPage()
      } catch (err) {
        addToast(toast, {
          severity: 'error',
          summary: 'Unable to delete analysis',
          detail: store.error ?? formatApiError(err),
        })
      }
    },
  })
}

function confirmRestore(asset: MaintainableAssetView): void {
  if (!asset.analysisId) return

  confirm.require({
    header: 'Restore Analysis',
    message: `Are you sure you want to restore analysis ${asset.analysisNumber}?`,
    icon: 'fa-solid fa-rotate-left',
    acceptLabel: 'Restore',
    rejectLabel: 'Cancel',
    acceptClass: 'p-button-success',
    accept: async () => {
      try {
        await store.restoreAnalysis(asset.analysisId!)
        addToast(toast, {
          severity: 'success',
          summary: 'Analysis restored',
          detail: `${asset.analysisNumber} was restored.`,
        })
        await fetchPage()
      } catch (err) {
        addToast(toast, {
          severity: 'error',
          summary: 'Unable to restore analysis',
          detail: store.error ?? formatApiError(err),
        })
      }
    },
  })
}

defineExpose({ navigateToAssignmentReview, confirmDelete, deletedRowClass })
</script>
